import { graphql, PageProps } from 'gatsby';
import React from 'react';

import { Seo, SidebarFilter } from '../components';
import ProductFilter from '../components/Filters/ProductFilter';
import ListProducts from '../components/Product/ListProducts';
import ListProductSearch from '../components/Product/ListProductSearch';
import useProductFilter from '../hooks/useProductFilter';
import useSiteMetadata from '../hooks/useSiteMetadata';
import {
  Brand,
  Category,
  Items,
  ProductRatingType,
  Products,
  ProductMinMaxPriceType,
  ProductsPricesType,
} from '../types';

interface Props extends PageProps {
  data: {
    allProducts: Products;
    products: Products;
    brands: Items<Brand>;
    categories: Items<Category>;
  };
  pageContext: {
    productsRating: ProductRatingType;
    productsPrices: ProductsPricesType;
    productsMinMaxPrice: ProductMinMaxPriceType;
  };
}

const ProductsTemplate: React.FC<Props> = ({ data, pageContext, location }) => {
  const { productsRating, productsPrices } = pageContext;
  const { siteTitle, siteDescription } = useSiteMetadata();

  const searchParams = new URLSearchParams(location.search);
  const { enabled: filterEnabled, products: filteredProducts } =
    useProductFilter(data.allProducts.nodes, searchParams, {
      productsRating,
      productsPrices,
    });
  const searchValue = searchParams.get('k');
  const isSearchFilterActive = !!searchValue;

  return (
    <>
      <Seo title={siteTitle} description={siteDescription} />
      <section className="container pt-3 pb-12 md:pt-8 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-9">
            {isSearchFilterActive ? (
              <ListProductSearch
                products={filteredProducts}
                searchValue={searchValue}
                pageContext={pageContext}
              />
            ) : (
              <ListProducts
                products={
                  filterEnabled ? filteredProducts : data.products.nodes
                }
                isAnyFilterActive={filterEnabled}
                pageContext={pageContext}
              />
            )}
          </div>
          <div className="md:col-span-3 order-last md:order-first">
            <ProductFilter searchParams={searchParams} />
            <SidebarFilter title="Filter by Brand" items={data.brands} />
            <SidebarFilter title="Filter by Category" items={data.categories} />
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductsTemplate;

export const query = graphql`
  query ProductsQuery(
    $mainTable: String!
    $hierarchyTable: String!
    $skip: Int!
    $limit: Int!
  ) {
    allProducts: allAirtable(
      filter: { table: { eq: $mainTable }, data: { slug: { ne: null } } }
    ) {
      nodes {
        data {
          name
          brand
          slug
          primaryImage
        }
      }
    }
    products: allAirtable(
      filter: { table: { eq: $mainTable }, data: { slug: { ne: null } } }
      skip: $skip
      limit: $limit
    ) {
      nodes {
        ...ProductsFragmentData
      }
    }
    brands: allAirtable(
      filter: {
        table: { eq: $hierarchyTable }
        data: { type: { eq: "brand" } }
      }
      sort: { data: { name: ASC } }
    ) {
      nodes {
        ...HierarchyFragmentData
      }
    }
    categories: allAirtable(
      filter: {
        table: { eq: $hierarchyTable }
        data: { type: { eq: "category" } }
      }
      sort: { data: { name: ASC } }
    ) {
      nodes {
        ...HierarchyFragmentData
      }
    }
  }
`;
